import React, { useCallback, useState } from "react";
import { SessionEventObservable } from "@/store/sessions/SessionEventObservable";
import styled from "@emotion/styled";
import { mdsColors, mdsFontSizes } from "@/design-system/foundations";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { css } from "@/domains/emotion";
import memLogomarkImage from "@/assets/images/mem-logomark.svg";
import { observer } from "mobx-react-lite";
import { SessionStatus } from "@/store/sessions/types";
import { SessionStatusIcon } from "@/components/active-session/SessionStatusIcon";
import { MdsIconButton } from "@/design-system/components/icon-button";
import {
  CommonMentionKindForRoutableKind,
  MemCommonMarkdown,
  OnClickMemItem,
} from "@mem-labs/common-editor";
import { useAppStore } from "@/store";
import { canRenderForm, InputForm } from "@/components/active-session/InputRequestForms";
import { getNextInputRequestValue } from "@/components/active-session/InputRequestHelpers";
import { FadeInAnimator } from "@/design-system/components/animation";

interface SessionProps {
  title: string;
  sessionId: string;
  status: SessionStatus;
  events: SessionEventObservable[];
  onDismiss?: () => void;
}

interface GroupedMessage {
  type: "single" | "reasoning_group";
  event?: SessionEventObservable;
  events?: SessionEventObservable[];
}

export const Session: React.FC<SessionProps> = observer(
  ({ title, status, events, onDismiss, sessionId }) => {
    const { store } = useAppStore();
    const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
    const [isCollapsed, setIsCollapsed] = useState(false);
    const isProcessing = status === "PROCESSING";
    const orderedMessageEvents = events
      .filter(event => event.modelData.kind === "MESSAGE_SENT")
      .toSorted((a, b) => a.locallyCreatedAt.getTime() - b.locallyCreatedAt.getTime());

    const orderedInputEvents = events
      .filter(
        event =>
          event.modelData.kind === "INPUT_REQUESTED" || event.modelData.kind === "INPUT_PROVIDED"
      )
      .toSorted((a, b) => a.locallyCreatedAt.getTime() - b.locallyCreatedAt.getTime());

    const nextInputRequestValue = getNextInputRequestValue(orderedInputEvents);
    const groupedMessages = groupUpMessageEventsByType(orderedMessageEvents);

    // Toggle the collapsed state of a specific reasoning group
    const toggleReasoningGroup = (groupIndex: number) => {
      setCollapsedGroups(prev => ({
        ...prev,
        [groupIndex]: !prev[groupIndex],
      }));
    };

    return (
      <Wrapper>
        <SessionTitle
          isCollapsed={isCollapsed}
          onClick={() => {
            if (!isCollapsed) return;
            setIsCollapsed(!isCollapsed);
          }}
        >
          <TitleAndStatusContainer>
            {title}
            <SessionStatusIcon status={status} />
          </TitleAndStatusContainer>
          <TitleAndStatusContainer>
            <MdsIconButton
              iconKind={
                isCollapsed
                  ? MdsIconKind.AngleDown
                  : !isProcessing
                    ? MdsIconKind.Exit
                    : MdsIconKind.AngleUp
              }
              onClick={() => {
                isCollapsed
                  ? setIsCollapsed(false)
                  : isProcessing
                    ? setIsCollapsed(true)
                    : onDismiss?.();
              }}
            />
          </TitleAndStatusContainer>
        </SessionTitle>

        {!isCollapsed && (
          <>
            <SessionBody>
              <Icon src={memLogomarkImage} alt="Mem" />
              {nextInputRequestValue && canRenderForm(nextInputRequestValue?.form) ? (
                <InputForm
                  inputRequestValue={nextInputRequestValue}
                  dismissSession={() => onDismiss?.()}
                  metadata={nextInputRequestValue.metadata}
                />
              ) : (
                <MessagesContainer>
                  {groupedMessages.map((group, groupIndex) => {
                    const isCollapsed = !!collapsedGroups[groupIndex];
                    if (group.type === "single" && group.event) {
                      return (
                        <div key={group.event.modelData.id}>
                          <SessionEvent event={group.event} />
                        </div>
                      );
                    } else if (
                      group.type === "reasoning_group" &&
                      group.events &&
                      group.events.length > 0
                    ) {
                      return (
                        <ReasoningGroup key={`reasoning-group-${groupIndex}`}>
                          <ReasoningHeader onClick={() => toggleReasoningGroup(groupIndex)}>
                            {isCollapsed ? "Show t" : "T"}hought process{" "}
                            <MdsIcon
                              innerStyles={{ Icon: { className: thoughtProcessChevronStyle } }}
                              kind={isCollapsed ? MdsIconKind.AngleDown : MdsIconKind.AngleUp}
                            />
                          </ReasoningHeader>
                          {!isCollapsed && (
                            <div>
                              {group.events.map(event => (
                                <FadeInAnimator
                                  key={event.modelData.id}
                                  element={<SessionEvent event={event} />}
                                />
                              ))}
                            </div>
                          )}
                        </ReasoningGroup>
                      );
                    }

                    return null;
                  })}
                </MessagesContainer>
              )}
            </SessionBody>
            {store.debug.debugModeEnabled && (
              <DebugContent
                onClick={() => {
                  navigator.clipboard.writeText(sessionId);
                }}
              >
                <DebugSessionId>
                  <MdsIcon
                    kind={MdsIconKind.Copy}
                    innerStyles={{
                      Icon: { className: css({ color: mdsColors().grey.x300, width: 8 }) },
                    }}
                  />
                  {sessionId}
                </DebugSessionId>
                <DismissLink
                  href="#"
                  onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();
                    onDismiss?.();
                  }}
                >
                  Dismiss
                </DismissLink>
              </DebugContent>
            )}
          </>
        )}
      </Wrapper>
    );
  }
);

Session.displayName = "Session";

interface SessionEventProps {
  event: SessionEventObservable;
}
const SessionEvent: React.FC<SessionEventProps> = ({ event }) => {
  const { store } = useAppStore();

  const handleClickMemItem: OnClickMemItem = useCallback(
    (itemKind, itemId, target) => {
      const mentionKind = CommonMentionKindForRoutableKind(itemKind);
      if (mentionKind) {
        store.navigation.goToMention({
          id: itemId,
          mentionKind,
          url: "",
          highlightText: target.textContent ?? undefined,
        });
      } else {
        if (itemKind === "imports") {
          const [importBatchId, listKind] = itemId.split("/");
          switch (listKind) {
            case "notes":
              store.navigation.goToImportBatchNotes({
                importBatchId: importBatchId,
              });
              break;
            case "collections":
              store.navigation.goToImportBatchCollections({
                importBatchId: importBatchId,
              });
              break;
          }
        }
      }
    },
    [store.navigation]
  );

  if (!event.eventValue.content && !event.modelData.event_value_json.content) return null;

  let content = event.eventValue.content as string;
  if (event.modelData.kind === "MESSAGE_SENT") {
    // Override the content if the event is a message sent event, since the display text
    // is stored in the event_value_json field.
    // TODO: Change event_value_json to more a generic related_model_data field.
    content = event.modelData.event_value_json.content as string;
  }

  switch (event.modelData.event_value_json.kind) {
    case "REASONING":
      return <StyledReasoningMessage>{content}</StyledReasoningMessage>;
    case "ACTION":
      return (
        <StyledActionMessage>
          <MemCommonMarkdown unstyled onClickMemItem={handleClickMemItem}>
            {content}
          </MemCommonMarkdown>
          <MdsIcon
            kind={MdsIconKind.Check}
            innerStyles={{ Icon: { className: actionStepCheckmarkStyle } }}
          />
        </StyledActionMessage>
      );
    case "COMMUNICATION":
      return <StyledCommunicationMessage>{content}</StyledCommunicationMessage>;
    default:
      return null;
  }
};

SessionEvent.displayName = "SessionEvent";

const groupUpMessageEventsByType = (messageEvents: SessionEventObservable[]) => {
  const groupedMessages: GroupedMessage[] = [];
  let currentReasoningGroup: SessionEventObservable[] = [];

  messageEvents.forEach(event => {
    if (event.eventValue.kind === "REASONING") {
      currentReasoningGroup.push(event);
    } else {
      if (currentReasoningGroup.length > 0) {
        groupedMessages.push({
          type: "reasoning_group",
          events: [...currentReasoningGroup],
        });
        currentReasoningGroup = [];
      }
      groupedMessages.push({
        type: "single",
        event,
      });
    }
  });

  // Add any remaining reasoning events
  if (currentReasoningGroup.length > 0) {
    groupedMessages.push({
      type: "reasoning_group",
      events: [...currentReasoningGroup],
    });
  }

  return groupedMessages;
};

const Wrapper = styled.div`
  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.grey.x100};
    margin-bottom: ${({ theme }) => theme.spacing.xs};
  }

  &:last-child {
    margin-bottom: ${({ theme }) => `-${theme.spacing.sm}`};
  }
`;

const SessionTitle = styled.h3<{ isCollapsed?: boolean }>(({ theme, isCollapsed }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  fontSize: theme.fontSizes.large,
  fontWeight: theme.fontWeights.semiBold,
  padding: `0 0 ${isCollapsed ? theme.spacing.xs : theme.spacing.sm} 0`,
  margin: 0,
  cursor: isCollapsed ? "pointer" : undefined,
}));

const TitleAndStatusContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const SessionBody = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing.md,
  padding: `${theme.spacing.sm} 0 0 0`,
  margin: `0 0 ${theme.spacing.sm} 0`,
}));

const MessagesContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: ${({ theme }) => theme.spacing.xs};
`;

const ReasoningGroup = styled.div(({ theme }) => ({
  marginBottom: theme.spacing.xs,
}));

const ReasoningHeader = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  cursor: "pointer",
  color: theme.colors.chat.header,
  fontSize: theme.fontSizes.xxsmall,
  lineHeight: theme.lineHeights.xsmall,
  fontWeight: theme.fontWeights.medium,
  marginBottom: theme.spacing.xs,

  "&:hover": {
    textDecoration: "underline",
  },
}));

const StyledReasoningMessage = styled.div(({ theme }) => ({
  color: theme.colors.chat.header,
  fontSize: theme.fontSizes.xxsmall,
  lineHeight: theme.lineHeights.xsmall,
  fontWeight: theme.fontWeights.medium,
}));

const StyledActionMessage = styled.div(({ theme }) => ({
  display: "inline-flex",
  flexDirection: "row",
  alignItems: "center",
  gap: theme.spacing.sm,
  marginBottom: theme.spacing.xs,
  color: theme.colors.grey.x875,
  fontSize: theme.fontSizes.smedium,
  lineHeight: theme.lineHeights.smedium,
  fontWeight: theme.fontWeights.regular,

  a: {
    color: theme.colors.grey.x875,
    textDecoration: "underline",
  },
}));

const StyledCommunicationMessage = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: theme.spacing.sm,
  marginBottom: theme.spacing.xs,
  color: theme.colors.grey.x875,
  fontSize: theme.fontSizes.smedium,
  lineHeight: theme.lineHeights.smedium,
  fontWeight: theme.fontWeights.regular,
}));

const thoughtProcessChevronStyle = css({
  color: mdsColors().chat.header,
  width: mdsFontSizes().xxsmall,
  height: mdsFontSizes().xxsmall,
});

const actionStepCheckmarkStyle = css({
  color: mdsColors().green.x500,
  width: mdsFontSizes().smedium,
  height: mdsFontSizes().smedium,
});

const Icon = styled.img(({ src }) => ({
  width: 24,
  height: 24,
  src,
}));

const DebugContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsPlus};

  font-size: ${({ theme }) => theme.fontSizes.xxxxsmall};
  color: ${({ theme }) => theme.colors.grey.x300};
  cursor: pointer;
`;

const DebugSessionId = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-left: ${({ theme }) => theme.spacing.xsPlus};
  padding-right: ${({ theme }) => theme.spacing.xsPlus};
  border-radius: ${({ theme }) => theme.borderRadius.medium + "px"};

  &:active {
    color: ${({ theme }) => theme.colors.grey.x400};
    background-color: ${({ theme }) => theme.colors.grey.x25};
  }
`;

const DismissLink = styled.a`
  color: ${({ theme }) => theme.colors.grey.x300};
  font-size: ${({ theme }) => theme.fontSizes.xxxxsmall};

  &:hover {
    text-decoration: underline;
  }
`;
